import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
  Progress,
  Divider,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FaGlobe,
  FaChartLine,
  FaComments,
  FaSmile,
  FaChartBar,
} from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { logger } from '../../utils/logger';
import { getAllImages } from '../../services/imageService';
import { getHistoricalTraffic } from '../../services/reviewService';
import {
  getTrafficValue,
  sortBusinessesBySeoScore,
} from '../../utils/competitiveIntelHelpers';
import BusinessReviewModal from '../BusinessReviewModal';
import BusinessIntelCard from '../core/BusinessIntelCard';

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search';
const GEOCODE_DELAY_MS = 1100;
const MAPS_AT_REGEX = /@(-?\d+\.?\d*),(-?\d+\.?\d*)/;
const MAPS_3D4D_REGEX = /!3d(-?\d+\.?\d+)!4d(-?\d+\.?\d+)/;

const COLOR_SCALES = {
  top: '#22C55E',
  high: '#84CC16',
  mid: '#EAB308',
  low: '#F97316',
  bottom: '#EF4444',
  noData: '#6B7280',
};

const COLOR_MODES = {
  growth: {
    label: 'Review Growth',
    icon: FaChartBar,
    getMetric: (b) => {
      const counts = b.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(counts);
      if (months.length === 0) return 0;
      return months.reduce((sum, m) => sum + (counts[m] || 0), 0);
    },
    levels: [],
    getColor: null,
  },
  sentiment: {
    label: 'Customer Sentiment',
    icon: FaSmile,
    getMetric: (b) => b.rating_avg || 0,
    levels: [
      { label: '4.5+ Stars', color: COLOR_SCALES.top },
      { label: '4.0+ Stars', color: COLOR_SCALES.high },
      { label: '3.5+ Stars', color: COLOR_SCALES.mid },
      { label: '3.0+ Stars', color: COLOR_SCALES.low },
      { label: '< 3.0 Stars', color: COLOR_SCALES.bottom },
    ],
    getColor: (val) => {
      if (val >= 4.5) return COLOR_SCALES.top;
      if (val >= 4.0) return COLOR_SCALES.high;
      if (val >= 3.5) return COLOR_SCALES.mid;
      if (val >= 3.0) return COLOR_SCALES.low;
      return COLOR_SCALES.bottom;
    },
  },
  totals: {
    label: 'Review Totals',
    icon: FaComments,
    getMetric: (b) => b.rating_count || 0,
    levels: [],
    getColor: null,
  },
  traffic: {
    label: 'Website Traffic',
    icon: FaGlobe,
    getMetric: (b) => b.traffic_data?.metrics?.estimated_monthly_visitors || 0,
    levels: [],
    getColor: null,
  },
};

function computePercentileColors(businesses, getMetric) {
  const values = businesses.map(getMetric).filter(v => v > 0).sort((a, b) => a - b);
  if (values.length === 0) return () => COLOR_SCALES.noData;

  const p20 = values[Math.floor(values.length * 0.2)] || 0;
  const p40 = values[Math.floor(values.length * 0.4)] || 0;
  const p60 = values[Math.floor(values.length * 0.6)] || 0;
  const p80 = values[Math.floor(values.length * 0.8)] || 0;

  return (val) => {
    if (val <= 0) return COLOR_SCALES.noData;
    if (val >= p80) return COLOR_SCALES.top;
    if (val >= p60) return COLOR_SCALES.high;
    if (val >= p40) return COLOR_SCALES.mid;
    if (val >= p20) return COLOR_SCALES.low;
    return COLOR_SCALES.bottom;
  };
}

function getPercentileLevels(businesses, getMetric, label) {
  const values = businesses.map(getMetric).filter(v => v > 0).sort((a, b) => a - b);
  if (values.length === 0) return [{ label: `No ${label} data`, color: COLOR_SCALES.noData }];

  const p80 = values[Math.floor(values.length * 0.8)] || 0;
  const p60 = values[Math.floor(values.length * 0.6)] || 0;
  const p40 = values[Math.floor(values.length * 0.4)] || 0;
  const p20 = values[Math.floor(values.length * 0.2)] || 0;

  const fmt = (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v));

  return [
    { label: `${fmt(p80)}+`, color: COLOR_SCALES.top },
    { label: `${fmt(p60)}–${fmt(p80)}`, color: COLOR_SCALES.high },
    { label: `${fmt(p40)}–${fmt(p60)}`, color: COLOR_SCALES.mid },
    { label: `${fmt(p20)}–${fmt(p40)}`, color: COLOR_SCALES.low },
    { label: `< ${fmt(p20)}`, color: COLOR_SCALES.bottom },
  ];
}

function createNeonDotSvg(color) {
  const uid = Math.random().toString(36).slice(2, 8);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
    <defs>
      <radialGradient id="glow-outer-${uid}" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="${color}" stop-opacity="0.35"/>
        <stop offset="60%" stop-color="${color}" stop-opacity="0.12"/>
        <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="glow-mid-${uid}" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="${color}" stop-opacity="0.7"/>
        <stop offset="50%" stop-color="${color}" stop-opacity="0.25"/>
        <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="glow-core-${uid}" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="white" stop-opacity="0.95"/>
        <stop offset="40%" stop-color="${color}" stop-opacity="0.9"/>
        <stop offset="100%" stop-color="${color}" stop-opacity="0.5"/>
      </radialGradient>
      <filter id="blur-${uid}" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="3"/>
      </filter>
    </defs>
    <circle cx="24" cy="24" r="22" fill="url(#glow-outer-${uid})"/>
    <circle cx="24" cy="24" r="14" fill="url(#glow-mid-${uid})" filter="url(#blur-${uid})"/>
    <circle cx="24" cy="24" r="7" fill="url(#glow-core-${uid})"/>
    <circle cx="24" cy="24" r="3.5" fill="white" opacity="0.9"/>
  </svg>`;
}

function createLabeledPinIcon(business, color, imageUrl) {
  const safeName = (business.name || 'Unknown')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  const displayName = business.name.length > 20
    ? business.name.substring(0, 20) + '…'
    : business.name;
  const safeDisplayName = displayName
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const imgHtml = imageUrl
    ? `<img src="${imageUrl}" style="width:22px;height:22px;border-radius:50%;object-fit:cover;flex-shrink:0;border:1.5px solid ${color};box-shadow:0 0 6px ${color};" onerror="this.style.display='none'" />`
    : '';

  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;">
      ${createNeonDotSvg(color)}
      <div style="display:flex;align-items:center;gap:5px;background:rgba(10,10,26,0.88);border:1px solid ${color}44;border-radius:8px;padding:4px 10px 4px ${imageUrl ? '4px' : '10px'};margin-top:2px;box-shadow:0 0 12px ${color}55, 0 0 4px ${color}33;max-width:200px;backdrop-filter:blur(6px);">
        ${imgHtml}
        <span style="font-size:12.5px;font-weight:600;color:#f0f0f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:150px;line-height:1.3;text-shadow:0 0 8px ${color}88, 0 0 2px ${color}44;" title="${safeName}">${safeDisplayName}</span>
      </div>
    </div>
  `;

  return L.divIcon({
    html,
    className: 'competitor-map-pin',
    iconSize: [200, 80],
    iconAnchor: [100, 40],
    tooltipAnchor: [0, -44],
  });
}

function MapAutoFit({ positions }) {
  const map = useMap();

  useEffect(() => {
    if (positions.length === 0) return;

    if (positions.length === 1) {
      map.setView(positions[0], 14);
      return;
    }

    const bounds = L.latLngBounds(positions);
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
  }, [positions, map]);

  return null;
}

function toNum(v) {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function validCoord(lat, lng) {
  return lat != null && lng != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

function extractCoordsFromBusiness(business) {
  let lat = toNum(business.latitude);
  let lng = toNum(business.longitude);
  if (validCoord(lat, lng)) return { lat, lng };

  const loc = business.location;
  if (loc && typeof loc === 'object') {
    lat = toNum(loc.latitude) ?? toNum(loc.lat);
    lng = toNum(loc.longitude) ?? toNum(loc.lng);
    if (validCoord(lat, lng)) return { lat, lng };
  }

  const geo = business.geometry;
  if (geo && typeof geo === 'object' && geo.location && typeof geo.location === 'object') {
    lat = toNum(geo.location.lat) ?? toNum(geo.location.latitude);
    lng = toNum(geo.location.lng) ?? toNum(geo.location.longitude);
    if (validCoord(lat, lng)) return { lat, lng };
  }

  lat = toNum(business.lat);
  lng = toNum(business.lng);
  if (validCoord(lat, lng)) return { lat, lng };

  const mapsUri = business.maps_uri || '';
  if (mapsUri) {
    let match = mapsUri.match(MAPS_AT_REGEX);
    if (match) {
      lat = parseFloat(match[1]);
      lng = parseFloat(match[2]);
      if (validCoord(lat, lng)) return { lat, lng };
    }

    match = mapsUri.match(MAPS_3D4D_REGEX);
    if (match) {
      lat = parseFloat(match[1]);
      lng = parseFloat(match[2]);
      if (validCoord(lat, lng)) return { lat, lng };
    }
  }

  return null;
}

function cleanAddressForGeocoding(address) {
  if (!address) return null;
  let cleaned = address.trim();
  if (!cleaned) return null;

  cleaned = cleaned.replace(/\bSuite\s+\S+/i, '').replace(/\bSte\.?\s+\S+/i, '');
  cleaned = cleaned.replace(/\bUnit\s+\S+/i, '').replace(/\bApt\.?\s+\S+/i, '');
  cleaned = cleaned.replace(/\bFloor\s+\S+/i, '').replace(/\bFl\.?\s+\S+/i, '');
  cleaned = cleaned.replace(/\bBldg\.?\s+\S+/i, '').replace(/\bBuilding\s+\S+/i, '');
  cleaned = cleaned.replace(/\bRoom\s+\S+/i, '').replace(/\bRm\.?\s+\S+/i, '');
  cleaned = cleaned.replace(/\b#\S+/g, '');
  cleaned = cleaned.replace(/\s{2,}/g, ' ').replace(/,\s*,/g, ',').trim();
  cleaned = cleaned.replace(/^,\s*/, '').replace(/,\s*$/, '');
  return cleaned || null;
}

function simplifyAddressForGeocoding(address) {
  if (!address) return null;
  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length <= 2) return null;
  return parts.slice(1).join(', ');
}

async function geocodeAddress(address) {
  try {
    const params = new URLSearchParams({
      q: address,
      format: 'json',
      limit: '1',
      addressdetails: '0',
    });

    const response = await fetch(`${NOMINATIM_BASE}?${params}`, {
      headers: { 'User-Agent': 'LocalyticsPlatform/1.0' },
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
      };
    }
    return null;
  } catch (err) {
    logger.error('Geocoding failed for address:', address, err);
    return null;
  }
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const getBusinessInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(word => word.length > 0);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  const skipWords = ['and', 'the', 'a', 'an', 'of', 'in', 'for', '&'];
  const significantWords = words.filter(w => !skipWords.includes(w.toLowerCase()));
  if (significantWords.length >= 2) return (significantWords[0][0] + significantWords[1][0]).toUpperCase();
  if (significantWords.length === 1) return significantWords[0].substring(0, 2).toUpperCase();
  return (words[0][0] + (words[1] ? words[1][0] : '')).toUpperCase();
};

const competitorMapCache = {
  dataFingerprint: null,
  geocodedBusinesses: [],
  imageMap: null,
  trafficHistory: null,
};

function computeDataFingerprint(businesses) {
  if (!businesses?.length) return null;
  const len = businesses.length;
  const first = businesses[0]?.maps_uri || businesses[0]?.name || '';
  const last = businesses[len - 1]?.maps_uri || businesses[len - 1]?.name || '';
  return `${len}:${first}:${last}`;
}


function ColorModeSelector({ colorMode, setColorMode, legendLevels }) {
  const modes = Object.entries(COLOR_MODES);

  return (
    <Box
      position="absolute"
      bottom={4}
      left={4}
      zIndex={1000}
      bg="rgba(15, 23, 42, 0.92)"
      border="1px solid"
      borderColor="gray.600"
      borderRadius="xl"
      px={4}
      py={3}
      backdropFilter="blur(12px)"
      maxW="250px"
      boxShadow="0 4px 24px rgba(0, 0, 0, 0.3)"
    >
      <VStack align="stretch" spacing={2.5}>
        <Text color="gray.300" fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
          Color By
        </Text>
        <VStack align="stretch" spacing={1}>
          {modes.map(([key, mode]) => (
            <Button
              key={key}
              size="sm"
              variant={colorMode === key ? 'solid' : 'ghost'}
              colorScheme={colorMode === key ? 'blue' : 'gray'}
              justifyContent="flex-start"
              leftIcon={<Icon as={mode.icon} boxSize={3.5} />}
              onClick={() => setColorMode(key)}
              fontWeight={colorMode === key ? 'bold' : 'normal'}
              color={colorMode === key ? 'white' : 'gray.400'}
              _hover={{ color: 'white', bg: colorMode === key ? undefined : 'whiteAlpha.100' }}
              borderRadius="md"
              px={2.5}
              h="32px"
              fontSize="sm"
            >
              {mode.label}
            </Button>
          ))}
        </VStack>
        <Divider borderColor="gray.600" />
        <VStack align="stretch" spacing={1}>
          {legendLevels.map((level, i) => (
            <HStack key={i} spacing={2}>
              <Box
                w={3}
                h={3}
                borderRadius="full"
                bg={level.color}
                flexShrink={0}
                boxShadow={`0 0 6px ${level.color}, 0 0 10px ${level.color}88`}
              />
              <Text color="gray.400" fontSize="xs">{level.label}</Text>
            </HStack>
          ))}
        </VStack>
      </VStack>
    </Box>
  );
}

function MapRefCapture({ mapRefOut }) {
  const map = useMap();
  useEffect(() => { mapRefOut.current = map; }, [map, mapRefOut]);
  return null;
}

function HoverCardOverlay({ business, imageUrl, trafficData }) {
  return (
    <Box
      position="absolute"
      top="50%"
      right={12}
      zIndex={1001}
      pointerEvents="none"
      maxH="calc(100% - 60px)"
      overflowY="auto"
      opacity={business ? 1 : 0}
      transform={business ? 'translateY(-50%)' : 'translateX(20px) translateY(-50%)'}
      transition="opacity 0.2s ease-out, transform 0.2s ease-out"
      sx={{
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bg: 'gray.300', borderRadius: 'full' },
      }}
    >
      {business && (
        <BusinessIntelCard
          business={business}
          trafficData={trafficData}
          variant="hover"
          glowOnHover={false}
        />
      )}
    </Box>
  );
}

function CompetitorMapDashboard({ reviewsData, yearData = [], demoMode, demoData } = {}) {
  const [geocodedBusinesses, setGeocodedBusinesses] = useState([]);
  const [geocodingProgress, setGeocodingProgress] = useState(0);
  const [isGeocoding, setIsGeocoding] = useState(true);
  const [totalToGeocode, setTotalToGeocode] = useState(0);
  const [colorMode, setColorMode] = useState('growth');
  const [imageMap, setImageMap] = useState({});
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [trafficHistory, setTrafficHistory] = useState({});
  const [hoveredBusiness, setHoveredBusiness] = useState(null);
  const [hoveredImageUrl, setHoveredImageUrl] = useState(null);
  const [hoveredTrafficData, setHoveredTrafficData] = useState(null);
  const geocodingRef = useRef(false);
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (demoMode) return;
    if (competitorMapCache.imageMap) {
      setImageMap(competitorMapCache.imageMap);
      return;
    }
    let mounted = true;
    const fetchImages = async () => {
      try {
        const images = await getAllImages();
        if (!mounted) return;
        const map = {};
        if (images && Array.isArray(images)) {
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              map[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
        }
        competitorMapCache.imageMap = map;
        setImageMap(map);
      } catch (err) {
        logger.error('Error fetching business images for map:', err);
      }
    };
    fetchImages();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (demoMode) return;
    if (competitorMapCache.trafficHistory) {
      setTrafficHistory(competitorMapCache.trafficHistory);
      return;
    }
    let mounted = true;
    const fetchTrafficData = async () => {
      try {
        const result = await getHistoricalTraffic();
        if (mounted && result?.businesses) {
          const trafficMap = {};
          result.businesses.forEach(biz => {
            if (biz.mapsUri && biz.timeSeries) {
              const chartData = biz.timeSeries
                .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth))
                .slice(-12)
                .map(point => ({
                  month: new Date(point.yearMonth + '-01T00:00:00').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                  organicClicks: point.organicClicks || 0,
                }));
              trafficMap[biz.mapsUri] = chartData;
            }
          });
          competitorMapCache.trafficHistory = trafficMap;
          setTrafficHistory(trafficMap);
        }
      } catch (err) {
        logger.error('Error fetching traffic data for map:', err);
      }
    };
    fetchTrafficData();
    return () => { mounted = false; };
  }, []);

  const businessesForMap = useMemo(() => {
    if (!reviewsData) return [];
    return reviewsData.filter(b => {
      if (extractCoordsFromBusiness(b)) return true;
      if (b.address && b.address.trim().length > 0) return true;
      return false;
    });
  }, [reviewsData]);

  const geocodeAll = useCallback(async () => {
    if (geocodingRef.current) return;
    geocodingRef.current = true;

    const businesses = businessesForMap;
    setTotalToGeocode(businesses.length);
    setIsGeocoding(true);

    let needsDelay = false;
    const results = [];

    for (let i = 0; i < businesses.length; i++) {
      if (!geocodingRef.current) break;

      const biz = businesses[i];
      let coords = extractCoordsFromBusiness(biz);

      if (!coords && biz.address) {
        if (needsDelay) await delay(GEOCODE_DELAY_MS);
        coords = await geocodeAddress(biz.address);
        needsDelay = true;

        if (!coords) {
          const cleaned = cleanAddressForGeocoding(biz.address);
          if (cleaned && cleaned !== biz.address) {
            await delay(GEOCODE_DELAY_MS);
            coords = await geocodeAddress(cleaned);
          }
        }

        if (!coords) {
          const simplified = simplifyAddressForGeocoding(biz.address);
          if (simplified) {
            await delay(GEOCODE_DELAY_MS);
            coords = await geocodeAddress(simplified);
          }
        }
      }

      if (coords) {
        const geocoded = { ...biz, lat: coords.lat, lng: coords.lng };
        results.push(geocoded);
        setGeocodedBusinesses(prev => [...prev, geocoded]);
      }

      setGeocodingProgress(i + 1);
    }

    competitorMapCache.dataFingerprint = computeDataFingerprint(businesses);
    competitorMapCache.geocodedBusinesses = results;

    setIsGeocoding(false);
    geocodingRef.current = false;
  }, [businessesForMap]);

  useEffect(() => {
    if (demoMode) {
      const demoBiz = (demoData?.reviewsData || reviewsData || []).map(b => ({
        ...b,
        lat: b.latitude || b.lat,
        lng: b.longitude || b.lng,
      })).filter(b => b.lat && b.lng);
      setGeocodedBusinesses(demoBiz);
      setGeocodingProgress(demoBiz.length);
      setTotalToGeocode(demoBiz.length);
      setIsGeocoding(false);
      if (demoData?.trafficHistory) setTrafficHistory(demoData.trafficHistory);
      return;
    }
    if (businessesForMap.length > 0) {
      const fingerprint = computeDataFingerprint(businessesForMap);
      if (fingerprint && fingerprint === competitorMapCache.dataFingerprint && competitorMapCache.geocodedBusinesses.length > 0) {
        setGeocodedBusinesses(competitorMapCache.geocodedBusinesses);
        setGeocodingProgress(competitorMapCache.geocodedBusinesses.length);
        setTotalToGeocode(competitorMapCache.geocodedBusinesses.length);
        setIsGeocoding(false);
        return;
      }
      setGeocodedBusinesses([]);
      setGeocodingProgress(0);
      geocodingRef.current = false;
      geocodeAll();
    } else {
      setIsGeocoding(false);
    }

    return () => {
      geocodingRef.current = false;
    };
  }, [businessesForMap, geocodeAll]);

  const positions = useMemo(
    () => geocodedBusinesses.map(b => [b.lat, b.lng]),
    [geocodedBusinesses]
  );

  const yearDataByUri = useMemo(() => {
    const map = {};
    yearData.forEach(yd => { map[yd.maps_uri] = yd; });
    return map;
  }, [yearData]);

  const enrichedGeocodedBusinesses = useMemo(() => {
    return geocodedBusinesses.map(business => {
      const yearInfo = yearDataByUri[business.maps_uri];
      return {
        ...business,
        historicalData: yearInfo?.data || business.historicalData || {},
        insight: yearInfo?.insight || business.insight || '',
      };
    });
  }, [geocodedBusinesses, yearDataByUri]);

  const colorFn = useMemo(() => {
    const mode = COLOR_MODES[colorMode];
    if (mode.getColor) return mode.getColor;
    return computePercentileColors(enrichedGeocodedBusinesses, mode.getMetric);
  }, [colorMode, enrichedGeocodedBusinesses]);

  const legendLevels = useMemo(() => {
    const mode = COLOR_MODES[colorMode];
    if (mode.levels && mode.levels.length > 0) return mode.levels;
    return getPercentileLevels(enrichedGeocodedBusinesses, mode.getMetric, mode.label);
  }, [colorMode, enrichedGeocodedBusinesses]);

  const combinedData = useMemo(() => {
    if (!reviewsData?.length) return [];
    return reviewsData.map((currentData) => {
      const yearInfo = yearDataByUri[currentData.maps_uri];
      return {
        ...currentData,
        historicalData: yearInfo?.data || {
          monthlyReviewCounts: {},
          monthlyRatings: {},
          sentimentBreakdown: { positive: 0, neutral: 0, negative: 0 },
        },
      };
    });
  }, [reviewsData, yearDataByUri]);

  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
  }, [combinedData]);

  const businessSentimentData = useMemo(() => {
    if (!combinedData.length) return [];
    return combinedData.map((business) => {
      const sentimentBreakdown = business.historicalData
        ?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const total =
        sentimentBreakdown.positive +
        sentimentBreakdown.neutral +
        sentimentBreakdown.negative;
      const sentimentScore =
        total > 0
          ? ((sentimentBreakdown.positive + sentimentBreakdown.neutral * 0.5) /
              total) *
            100
          : 0;
      return { ...business, sentimentScore, ...sentimentBreakdown, total };
    });
  }, [combinedData]);

  const sortedBySentiment = useMemo(() => {
    if (!businessSentimentData.length) return [];
    return [...businessSentimentData].sort(
      (a, b) => b.sentimentScore - a.sentimentScore
    );
  }, [businessSentimentData]);

  const businessGrowthData = useMemo(() => {
    if (!combinedData.length) return [];
    return combinedData.map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(monthlyData).sort();
      const totalGrowth = months.reduce(
        (sum, month) => sum + (monthlyData[month] || 0),
        0
      );
      const growthRate = months.length > 0 ? totalGrowth / months.length : 0;
      const lastThreeMonths = months.length >= 3 ? months.slice(-3) : months;
      const threeMonthGrowth = lastThreeMonths.reduce(
        (sum, month) => sum + (monthlyData[month] || 0),
        0
      );
      return { ...business, threeMonthGrowth, totalGrowth, growthRate };
    });
  }, [combinedData]);

  const sortedByGrowth = useMemo(() => {
    if (!businessGrowthData.length) return [];
    return [...businessGrowthData].sort(
      (a, b) => b.totalGrowth - a.totalGrowth
    );
  }, [businessGrowthData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => {
        const trafficValue = getTrafficValue(business) || 0;
        return { ...business, trafficValue };
      })
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  const sortedBySeo = useMemo(
    () => sortBusinessesBySeoScore(combinedData),
    [combinedData]
  );

  const handleMarkerClick = useCallback((business) => {
    const businessData = combinedData.find(
      (b) => b.maps_uri === business.maps_uri
    );
    setSelectedBusiness(businessData || business);
    onOpen();
  }, [onOpen, combinedData]);

  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;

  if (!reviewsData || reviewsData.length === 0) {
    return (
      <Flex
        h="100vh"
        justify="center"
        align="center"
        bg="gray.900"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.400" thickness="4px" />
          <Text color="gray.400" fontSize="lg">Loading competitor data...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box
      ref={containerRef}
      position={{ base: "relative", md: "fixed" }}
      top={{ base: "auto", md: 0 }}
      left={{ base: "auto", md: "72px" }}
      right={{ base: "auto", md: 0 }}
      bottom={{ base: "auto", md: 0 }}
      zIndex={{ base: "auto", md: 1 }}
      h={{ base: "calc(100vh - 16px)", md: "auto" }}
      w={{ base: "calc(100% + 16px)", md: "auto" }}
      borderRadius={0}
      overflow="hidden"
      bg="#1a1a2e"
      mx={{ base: -2, md: 0 }}
      mt={{ base: -2, md: 0 }}
      mb={{ base: -2, md: 0 }}
      sx={{
        '.leaflet-container': {
          background: '#0a0a1a',
          height: '100%',
          width: '100%',
          borderRadius: 0,
        },
        '.leaflet-tile-pane': {
          filter: 'brightness(0.55) contrast(1.1) saturate(0.6)',
        },
        '.leaflet-tooltip': {
          background: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          padding: '0 !important',
          maxWidth: '420px !important',
        },
        '.leaflet-tooltip-top:before, .leaflet-tooltip-bottom:before, .leaflet-tooltip-left:before, .leaflet-tooltip-right:before': {
          display: 'none !important',
        },
        '.competitor-map-pin': {
          background: 'transparent',
          border: 'none',
        },
        '.leaflet-control-zoom': {
          border: 'none !important',
        },
        '.leaflet-control-zoom a': {
          background: '#1E293B !important',
          color: '#94A3B8 !important',
          border: '1px solid #334155 !important',
          borderRadius: '8px !important',
          width: '36px !important',
          height: '36px !important',
          lineHeight: '36px !important',
          fontSize: '18px !important',
        },
        '.leaflet-control-zoom a:hover': {
          background: '#334155 !important',
          color: '#E2E8F0 !important',
        },
        '.leaflet-control-attribution': {
          background: 'rgba(26, 26, 46, 0.85) !important',
          color: '#64748B !important',
          fontSize: '10px !important',
          padding: '2px 6px !important',
          borderRadius: '4px 0 0 0 !important',
        },
        '.leaflet-control-attribution a': {
          color: '#60A5FA !important',
        },
      }}
    >
      {isGeocoding && (
        <Box
          position="absolute"
          top={4}
          left="50%"
          transform="translateX(-50%)"
          zIndex={1000}
          bg="gray.900"
          border="1px solid"
          borderColor="blue.700"
          borderRadius="xl"
          px={6}
          py={3}
          boxShadow="0 4px 24px rgba(0, 100, 255, 0.2)"
          minW="280px"
        >
          <VStack spacing={2}>
            <HStack spacing={3}>
              <Spinner size="sm" color="blue.400" />
              <Text color="gray.300" fontSize="md">
                Mapping competitors... {geocodingProgress} / {totalToGeocode}
              </Text>
            </HStack>
            <Progress
              value={(geocodingProgress / Math.max(totalToGeocode, 1)) * 100}
              size="xs"
              colorScheme="blue"
              borderRadius="full"
              w="100%"
              bg="gray.700"
            />
          </VStack>
        </Box>
      )}

      <ColorModeSelector
        colorMode={colorMode}
        setColorMode={setColorMode}
        legendLevels={legendLevels}
      />

      <Box
        position="absolute"
        top={4}
        right={4}
        zIndex={1000}
        bg="rgba(15, 23, 42, 0.9)"
        border="1px solid"
        borderColor="gray.700"
        borderRadius="lg"
        px={4}
        py={2}
        backdropFilter="blur(8px)"
      >
        <HStack spacing={2}>
          <Icon as={FaChartLine} color="blue.400" boxSize={4} />
          <Text color="white" fontSize="md" fontWeight="semibold">
            {geocodedBusinesses.length}
          </Text>
          <Text color="gray.400" fontSize="md">
            competitors mapped
          </Text>
        </HStack>
      </Box>

      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution='&copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, Maxar, Earthstar Geographics'
          maxZoom={19}
        />
        <MapAutoFit positions={positions} />
        <MapRefCapture mapRefOut={mapRef} />
        {enrichedGeocodedBusinesses.map((business, index) => {
          const metric = COLOR_MODES[colorMode].getMetric(business);
          const pinColor = colorFn(metric);
          const imgUrl = imageMap[business.maps_uri];
          const bizTrafficData = trafficHistory[business.maps_uri] || null;

          return (
            <Marker
              key={`${business.name}-${index}`}
              position={[business.lat, business.lng]}
              icon={createLabeledPinIcon(business, pinColor, imgUrl)}
              eventHandlers={{
                click: () => handleMarkerClick(business),
                mouseover: () => {
                  setHoveredBusiness(business);
                  setHoveredImageUrl(imgUrl);
                  setHoveredTrafficData(bizTrafficData);
                },
                mouseout: () => {
                  setHoveredBusiness(null);
                },
              }}
            />
          );
        })}
      </MapContainer>

      <HoverCardOverlay
        business={hoveredBusiness}
        imageUrl={hoveredImageUrl}
        trafficData={hoveredTrafficData}
      />

      <Text
        position="absolute"
        bottom={2}
        right={2}
        zIndex={1000}
        color="gray.400"
        fontSize="sm"
        fontStyle="italic"
        bg="rgba(15, 23, 42, 0.85)"
        px={3}
        py={1}
        borderRadius="md"
        backdropFilter="blur(8px)"
      >
        {reviewsData && geocodedBusinesses.length < reviewsData.length
          ? `${reviewsData.length - geocodedBusinesses.length} competitor${reviewsData.length - geocodedBusinesses.length === 1 ? '' : 's'} could not be mapped (no address or coordinates available)`
          : 'All competitors mapped'}
      </Text>

      <BusinessReviewModal
        isOpen={isOpen}
        onClose={onClose}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        sortedBySeo={sortedBySeo}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
}

export default CompetitorMapDashboard;
